import { typography, radius } from '@/shared/theme/tokens';
import { HUD, statusOf } from './hudTheme';

const MONO = typography.fontFamily.mono;
const LABEL = {
  fontFamily: MONO, fontSize: typography.fontSize.xs,
  letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase',
};

/**
 * Telemetry strip — the thin command header: deployment identity, feed mode
 * (LIVE/DEMO), data freshness, and the mission clock. Pure status chrome.
 */
export function TelemetryStrip({ deploymentName, clock, jitterActive, animate, freshness }) {
  const time = clock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const date = clock.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase();

  const live = statusOf(jitterActive ? 'warning' : 'ok', false);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16, height: 40, padding: '0 14px', flexShrink: 0,
      background: HUD.panel, border: `1px solid ${HUD.stroke}`, borderRadius: radius.md,
      backdropFilter: 'blur(6px)', color: HUD.inkDim, ...LABEL,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: HUD.accent }}>radar</span>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.widest, color: HUD.ink }}>
          MISSION CONTROL
        </span>
      </span>

      <Sep />

      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: live.color }}>
        <Dot color={live.color} glow={live.glow} animate={animate} />
        {jitterActive ? 'DEMO' : 'LIVE'}
      </span>

      <Sep />

      <span style={{
        color: HUD.ink, letterSpacing: typography.letterSpacing.wide,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {deploymentName || 'CONTROL ROOM'}
      </span>

      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 14 }}>
        <Freshness freshness={freshness} animate={animate} />
        <Sep />
        <span style={{ color: HUD.ink, fontVariantNumeric: 'tabular-nums', letterSpacing: typography.letterSpacing.wide }}>{time}</span>
        <span style={{ color: HUD.inkFaint }}>{date}</span>
      </span>
    </div>
  );
}

function Freshness({ freshness, animate }) {
  const { stale, ageSeconds } = freshness;
  const meta = statusOf(stale ? 'warning' : 'ok', false);
  const ageLabel = ageSeconds < 2 ? 'NOW' : `${ageSeconds}s`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: meta.color }}>
      <Dot color={meta.color} glow={meta.glow} animate={animate && !stale} />
      <span>{stale ? 'STALE' : 'DATA'}</span>
      <span style={{ color: HUD.inkFaint, fontVariantNumeric: 'tabular-nums' }}>{ageLabel}</span>
    </span>
  );
}

function Dot({ color, glow, animate }) {
  return (
    <span style={{
      width: 7, height: 7, borderRadius: radius.full, background: color, flexShrink: 0,
      boxShadow: `0 0 7px ${glow}`,
      animation: animate ? 'hud-pulse 1.9s steps(1, end) infinite' : 'none',
    }}
    />
  );
}

function Sep() {
  return <span style={{ color: HUD.stroke }}>│</span>;
}
